import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card, Table, Button, InputNumber, Select, Space, Tag, Typography,
  message, Empty, Divider, Statistic, Row, Col, Avatar, Form, Modal, Input, Checkbox, Radio, Collapse
} from 'antd';
import {
  DollarOutlined, GoldOutlined, CalculatorOutlined, SaveOutlined,
  HistoryOutlined, UserOutlined, DeleteOutlined, ReloadOutlined,
  PlusOutlined, GiftOutlined, CheckOutlined, CloseOutlined, ClockCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { getItemIcon } from './constants';

const { Text } = Typography;
const { Option } = Select;

const API_BASE_URL = '';

const formatDate = (t) => {
  if (!t) return '未知日期';
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const BonusBank = ({ currentUser }) => {
  const [historyList, setHistoryList] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runDetails, setRunDetails] = useState(null);
  const [mesoAmount, setMesoAmount] = useState(0);
  const [coinAmount, setCoinAmount] = useState(0);
  const [distributions, setDistributions] = useState([]);
  // 加载已分配 run 后触发一次分列重算的标记（还原 R1/R2 收益与比例显示）
  const pendingRecalcRef = useRef(false);
  // 自定义罚款：选中的角色名、罚款 meso/coin 数额
  const [penaltyRows, setPenaltyRows] = useState([]);
  const [penaltyMeso, setPenaltyMeso] = useState(0);
  const [penaltyCoin, setPenaltyCoin] = useState(0);
  const [characterBalances, setCharacterBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [historyLimit, setHistoryLimit] = useState(10); // 历史记录限制
  
  // 掉落物品相关状态 - 分 Round 管理
  const [lootConfig, setLootConfig] = useState({ items: [], serviceTypes: [], roleBonus: [] });
  const [bossConfig, setBossConfig] = useState(null);
  const [round1Loot, setRound1Loot] = useState({ items: [], services: [] });
  const [round2Loot, setRound2Loot] = useState({ items: [], services: [] });
  const [addLootForm] = Form.useForm();
  const [addServiceForm] = Form.useForm();

  // 苹果消耗分配相关状态
  const DEFAULT_APPLE_PRICE = 2200000;
  const [appleEnabled, setAppleEnabled] = useState(false);
  const [applePrice, setApplePrice] = useState(DEFAULT_APPLE_PRICE);
  const [appleConsumption, setAppleConsumption] = useState([]); // [{ round, characterName, quantity }]
  const [appleShareModeR1, setAppleShareModeR1] = useState('even'); // 'even' | 'specific'
  const [appleShareModeR2, setAppleShareModeR2] = useState('even');
  
  // Host 权限相关
  const [hostUsernames, setHostUsernames] = useState([]);
  const isHost = useMemo(() => {
    if (!currentUser || hostUsernames.length === 0) return false;
    return hostUsernames.includes(currentUser.username);
  }, [currentUser, hostUsernames]);

  // 管理员相关
  const [isAdmin, setIsAdmin] = useState(false);
  const [pendingDistributions, setPendingDistributions] = useState([]);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [rejectingRunId, setRejectingRunId] = useState(null);

  // 加载掉落物品配置
  const loadLootConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loot-config`);
      if (res.ok) {
        setLootConfig(await res.json());
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // 加载 Boss 配置（获取状态比例）
  const loadBossConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/boss-config`);
      if (res.ok) {
        setBossConfig(await res.json());
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // 加载历史运行记录
  const loadHistory = useCallback(async (limit = historyLimit) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/history?limit=${limit}`);
      if (res.ok) {
        const data = await res.json();
        setHistoryList(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [historyLimit]);

  // 加载全部历史记录
  const loadAllHistory = useCallback(async () => {
    setHistoryLimit(0);
    try {
      const res = await fetch(`${API_BASE_URL}/api/history?limit=0`);
      if (res.ok) {
        const data = await res.json();
        setHistoryList(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // 加载角色余额
  const loadCharacterBalances = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/balances`);
      if (res.ok) {
        const data = await res.json();
        setCharacterBalances(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  // 交易记录分页
  const [transactionLimit, setTransactionLimit] = useState(20);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);

  // 加载交易记录（支持分页）
  const loadTransactions = useCallback(async (limit = transactionLimit) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/transactions?limit=${limit}`);
      if (res.ok) {
        const data = await res.json();
        setTransactions(data);
        // 如果返回的数量小于请求的数量，说明没有更多数据了
        setHasMoreTransactions(data.length >= limit);
      }
    } catch (e) {
      console.error(e);
    }
  }, [transactionLimit]);

  // 加载更多交易记录
  const loadMoreTransactions = useCallback(async () => {
    const newLimit = transactionLimit + 20;
    setTransactionLimit(newLimit);
    await loadTransactions(newLimit);
  }, [transactionLimit, loadTransactions]);

  // 检查是否是管理员（公开接口，不走 token 鉴权）
  const checkAdminStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${encodeURIComponent(currentUser?.username)}/check-admin`);
      if (res.ok) {
        const data = await res.json();
        setIsAdmin(data.isAdmin || false);
      }
    } catch (e) {
      console.error(e);
    }
  }, [currentUser?.username]);

  // 加载待确认的分配记录
  const loadPendingDistributions = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/pending-distributions?username=${currentUser?.username}`);
      if (res.ok) {
        const data = await res.json();
        setPendingDistributions(data);
      }
    } catch (e) {
      console.error(e);
    }
  }, [isAdmin, currentUser?.username]);

  useEffect(() => {
    loadHistory();
    loadCharacterBalances();
    loadTransactions();
    loadLootConfig();
    loadBossConfig();
    checkAdminStatus();
  }, [loadHistory, loadCharacterBalances, loadTransactions, loadLootConfig, loadBossConfig, checkAdminStatus]);

  // 管理员加载待确认列表
  useEffect(() => {
    if (isAdmin) {
      loadPendingDistributions();
    }
  }, [isAdmin, loadPendingDistributions]);

  // 加载历史记录的掉落物品数据
  const loadHistoryLoot = useCallback(async (runId) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${runId}/loot`);
      if (res.ok) {
        const data = await res.json();
        // 分 Round 设置数据
        setRound1Loot({
          items: data.round1Items || data.lootItems || [],
          services: data.round1Services || data.serviceIncome || []
        });
        setRound2Loot({
          items: data.round2Items || [],
          services: data.round2Services || []
        });
        // 设置 Host 用户名列表
        setHostUsernames(data.hostUsernames || []);
        // 设置苹果消耗数据
        setAppleEnabled(!!data.appleEnabled);
        setApplePrice(typeof data.applePrice === 'number' && data.applePrice > 0 ? data.applePrice : DEFAULT_APPLE_PRICE);
        // 兼容旧数据：无 round 字段则默认为 Round 1
        setAppleConsumption(
          Array.isArray(data.appleConsumption)
            ? data.appleConsumption.map(a => ({ round: a.round === 2 ? 2 : 1, characterName: a.characterName, quantity: a.quantity }))
            : []
        );
        setAppleShareModeR1(data.appleShareModeR1 === 'specific' ? 'specific' : 'even');
        setAppleShareModeR2(data.appleShareModeR2 === 'specific' ? 'specific' : 'even');

        // 返回完整数据，包括各 Round 的总额
        return {
          round1Items: data.round1Items || data.lootItems || [],
          round1Services: data.round1Services || data.serviceIncome || [],
          round2Items: data.round2Items || [],
          round2Services: data.round2Services || [],
          round1Meso: data.round1Meso || 0,
          round1Coin: data.round1Coin || 0,
          round2Meso: data.round2Meso || 0,
          round2Coin: data.round2Coin || 0,
          totalMeso: data.totalMeso || 0,
          totalCoin: data.totalCoin || 0,
          appleEnabled: !!data.appleEnabled,
          applePrice: typeof data.applePrice === 'number' && data.applePrice > 0 ? data.applePrice : DEFAULT_APPLE_PRICE,
          appleConsumption: Array.isArray(data.appleConsumption) ? data.appleConsumption : [],
          hostUsernames: data.hostUsernames || [],
          hostCharacters: data.hostCharacters || []
        };
      }
    } catch (e) {
      console.error(e);
    }
    return {
      round1Items: [], round1Services: [],
      round2Items: [], round2Services: [],
      round1Meso: 0, round1Coin: 0,
      round2Meso: 0, round2Coin: 0,
      totalMeso: 0, totalCoin: 0,
      appleEnabled: false, applePrice: DEFAULT_APPLE_PRICE, appleConsumption: [],
      appleShareModeR1: 'even', appleShareModeR2: 'even'
    };
  }, []);

  // 保存苹果消耗数据（仅苹果相关字段）
  const saveAppleData = async () => {
    if (!selectedRun) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${selectedRun}/loot?username=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appleEnabled: appleEnabled,
          applePrice: applePrice,
          appleConsumption: appleConsumption,
          appleShareModeR1: appleShareModeR1,
          appleShareModeR2: appleShareModeR2
        })
      });

      if (res.ok) {
        const totalQty = (appleConsumption || []).reduce((s, a) => s + (parseInt(a.quantity, 10) || 0), 0);
        const totalCost = totalQty * (applePrice || 0);
        messageApi.success(`苹果消耗已保存：${totalQty} 个 / -${totalCost.toLocaleString()} Meso`);
      } else {
        messageApi.error('保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败: ' + e.message);
    }
    setLoading(false);
  };

  // 保存掉落物品数据
  const saveLootData = async () => {
    if (!selectedRun) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/history/${selectedRun}/loot?username=${encodeURIComponent(currentUser?.username || '')}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          round1Items: round1Loot.items,
          round1Services: round1Loot.services,
          round2Items: round2Loot.items,
          round2Services: round2Loot.services,
          appleEnabled: appleEnabled,
          applePrice: applePrice,
          appleConsumption: appleConsumption,
          appleShareModeR1: appleShareModeR1,
          appleShareModeR2: appleShareModeR2
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        messageApi.success(`掉落物品已保存，R1: ${data.round1Meso?.toLocaleString() || 0} Meso / ${data.round1Coin || 0} Coin，R2: ${data.round2Meso?.toLocaleString() || 0} Meso / ${data.round2Coin || 0} Coin`);
        loadHistory(); // 刷新历史列表
      } else {
        messageApi.error('保存失败');
      }
    } catch (e) {
      messageApi.error('保存失败: ' + e.message);
    }
    setLoading(false);
  };

  // 选择运行记录
  const handleSelectRun = async (runId) => {
    if (!runId) {
      setSelectedRun(null);
      setRunDetails(null);
      setDistributions([]);
      setRound1Loot({ items: [], services: [] });
      setRound2Loot({ items: [], services: [] });
      setMesoAmount(0);
      setCoinAmount(0);
      setHostUsernames([]);
      setAppleEnabled(false);
      setApplePrice(DEFAULT_APPLE_PRICE);
      setAppleConsumption([]);
      setAppleShareModeR1('even');
      setAppleShareModeR2('even');
      return;
    }
    
    setLoading(true);
    try {
      // 并行加载运行详情和掉落物品
      const [detailRes, lootData] = await Promise.all([
        fetch(`${API_BASE_URL}/api/history/${runId}`),
        loadHistoryLoot(runId)
      ]);
      
      if (detailRes.ok) {
        const data = await detailRes.json();
        setSelectedRun(runId);
        setRunDetails(data);
        
        // 设置掉落物品计算的总额
        // 如果已分配，优先使用保存的分配总额
        if (data.distributed) {
          setMesoAmount(data.mesoTotal || lootData.totalMeso || data.totalMeso || 0);
          setCoinAmount(data.coinTotal || lootData.totalCoin || data.totalCoin || 0);
        } else {
          setMesoAmount(lootData.totalMeso || data.totalMeso || 0);
          setCoinAmount(lootData.totalCoin || data.totalCoin || 0);
        }
        
        // 计算每个角色的可分配 Round 数
        // 规则：参加了且状态不是 NotDone，不是全部 Abandoned，且至少有一个 AllDone
        const charRounds = data.characters.map(c => {
          // R1: 参加了且状态不是 NotDone
          const r1Participated = c.round1 && c.round1Status && c.round1Status !== 'NotDone';
          const r1Abandoned = c.round1Status === 'Abandoned';
          const r1Done = c.round1Status === 'AllDone';
          
          // R2: 参加了且状态不是 NotDone
          const r2Participated = c.round2 && c.round2Status && c.round2Status !== 'NotDone';
          const r2Abandoned = c.round2Status === 'Abandoned';
          const r2Done = c.round2Status === 'AllDone';
          
          // 检查是否全部 Abandoned（如果参加的 Round 全是 Abandoned，则不能分配）
          const participatedRounds = [];
          if (r1Participated) participatedRounds.push({ abandoned: r1Abandoned, done: r1Done });
          if (r2Participated) participatedRounds.push({ abandoned: r2Abandoned, done: r2Done });
          
          const allAbandoned = participatedRounds.length > 0 && participatedRounds.every(r => r.abandoned);
          const hasAnyDone = participatedRounds.some(r => r.done);
          
          // 可分配的 Round 数：参加了、不是 NotDone、不是全部 Abandoned、至少有一个 AllDone
          let roundCount = 0;
          if (!allAbandoned && hasAnyDone) {
            if (r1Participated && !r1Abandoned) roundCount++;
            if (r2Participated && !r2Abandoned) roundCount++;
          }
          
          // 获取角色定位（兼容 roles 数组和 role 字符串）
          // 保留第一个角色用于显示，但 roles 数组用于计算累加分成
          const characterRole = Array.isArray(c.roles) && c.roles.length > 0 
            ? c.roles[0] 
            : (c.role || null);
          
          // 如果已分配，从 distributionDetails 中获取分配结果
          let savedMeso = 0, savedCoin = 0;
          if (data.distributed && data.distributionDetails) {
            const savedDist = data.distributionDetails.find(d => d.characterName === c.name);
            if (savedDist) {
              savedMeso = savedDist.meso || 0;
              savedCoin = savedDist.coin || 0;
            }
          }
          
          return {
            characterName: c.name,
            team: c.team,
            role: characterRole,  // 角色定位（取第一个）
            roles: c.roles || [],  // 所有角色定位
            round1: c.round1,  // 是否参加了 R1
            round1Status: c.round1Status,  // R1 状态
            round1Countable: r1Participated && !r1Abandoned && hasAnyDone && !allAbandoned,  // R1 是否可计入分配
            round2: c.round2,  // 是否参加了 R2
            round2Status: c.round2Status,  // R2 状态
            round2Countable: r2Participated && !r2Abandoned && hasAnyDone && !allAbandoned,  // R2 是否可计入分配
            roundCount: roundCount,  // 可分配的 Round 数
            meso: savedMeso,  // 已保存的分配金额
            coin: savedCoin,  // 已保存的分配金额
            bonusMeso: 0,
            bonusCoin: 0
          };
        });
        setDistributions(charRounds);
        // 选中运行记录后：数据就绪时自动计算一次，填充每个角色的 R1/R2 分列收益与比例（无需手动点“计算分配”）
        pendingRecalcRef.current = true;
      }
    } catch (e) {
      console.error(e);
      messageApi.error('加载运行记录失败');
    }
    setLoading(false);
  };

  // 获取状态的分配比例
  const getStatusRatio = useCallback((status, bossType) => {
    if (!bossConfig || !bossConfig[bossType]) return 0;
    const statusConfig = bossConfig[bossType].statuses.find(s => s.name === status);
    return statusConfig?.ratio || 0;
  }, [bossConfig]);

  // 获取角色定位的额外分成比例 (根据 Boss 类型)
  const getRoleBonusPercent = useCallback((role, bossType) => {
    const roleBonus = lootConfig.roleBonus;
    // 兼容旧格式（数组）和新格式（按 Boss 类型的对象）
    let roleList = [];
    if (Array.isArray(roleBonus)) {
      roleList = roleBonus;
    } else if (roleBonus && roleBonus[bossType]) {
      roleList = roleBonus[bossType];
    }
    const roleConfig = roleList.find(rb => rb.role === role);
    return roleConfig?.bonusPercent || 0;
  }, [lootConfig.roleBonus]);

  // 计算单个 Round 的掉落物品总价值（包含交易税）
  const calculateRoundLootValue = useCallback((roundLoot) => {
    let totalMeso = 0;
    let totalCoin = 0;
    let totalTax = 0;  // 交易税总额
    
    // 计算物品价值（支持自定义售卖方式）
    (roundLoot.items || []).forEach(item => {
      const itemConfig = (lootConfig.items || []).find(i => i.name === item.name);
      const qty = item.quantity || 1;
      
      if (item.sellMethod === 'NPC' || !item.sellMethod) {
        // NPC 售卖：使用配置价值
        if (itemConfig) {
          totalMeso += (itemConfig.mesoValue || 0) * qty;
          totalCoin += (itemConfig.coinValue || 0) * qty;
        }
      } else {
        // FM/Others：使用自定义价格，Meso 需扣除 2.5% 交易税
        const rawMeso = (item.customMeso || 0) * qty;
        if (rawMeso > 0) {
          // 直接计算税后金额，使用 Math.round 确保整数结果
          const mesoAfterTax = Math.round(rawMeso * 0.975);
          const tax = rawMeso - mesoAfterTax;
          totalTax += tax;
          totalMeso += mesoAfterTax;
        }
        totalCoin += (item.customCoin || 0) * qty;
      }
    });
    
    // 计算服务收入（Meso 服务收入也需要扣除 2.5% 交易税）
    (roundLoot.services || []).forEach(service => {
      const serviceMeso = service.meso || 0;
      if (serviceMeso > 0) {
        // 直接计算税后金额
        const mesoAfterTax = Math.round(serviceMeso * 0.975);
        const serviceTax = serviceMeso - mesoAfterTax;
        totalTax += serviceTax;
        totalMeso += mesoAfterTax;
      }
      totalCoin += service.coin || 0;
    });
    
    return { meso: totalMeso, coin: totalCoin, tax: totalTax };
  }, [lootConfig.items]);

  // 计算分配 - 新算法
  const calculateDistribution = async (skipSave = false) => {
    if (!distributions.length || !runDetails) return;

    // 先保存掉落数据（已分配 run 的自动重算不写回历史，传 skipSave=true 跳过）
    if (selectedRun && !skipSave) {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/history/${selectedRun}/loot?username=${encodeURIComponent(currentUser?.username || '')}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            round1Items: round1Loot.items,
            round1Services: round1Loot.services,
            round2Items: round2Loot.items,
            round2Services: round2Loot.services,
            appleEnabled: appleEnabled,
            applePrice: applePrice,
            appleConsumption: appleConsumption,
            appleShareModeR1: appleShareModeR1,
            appleShareModeR2: appleShareModeR2
          })
        });
        
        if (!res.ok) {
          messageApi.error('保存掉落数据失败');
          setLoading(false);
          return;
        }
      } catch (e) {
        messageApi.error('保存掉落数据失败: ' + e.message);
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    
    const bossType = runDetails.bossType || 'HT';

    // 计算每个 Round 的收益
    let round1Value = calculateRoundLootValue(round1Loot);
    let round2Value = calculateRoundLootValue(round2Loot);

    // 检查是否有参与者
    const r1Participants = distributions.filter(d => d.round1 && d.round1Status && d.round1Status !== 'NotDone' && d.round1Status !== 'Abandoned');
    const r2Participants = distributions.filter(d => d.round2 && d.round2Status && d.round2Status !== 'NotDone' && d.round2Status !== 'Abandoned');
    
    // 检查每个 Round 是否有任何 AllDone 状态
    // 如果没有 AllDone，说明该 Round 没有完成，收益为 0
    const r1HasAllDone = distributions.some(d => d.round1 && d.round1Status === 'AllDone');
    const r2HasAllDone = distributions.some(d => d.round2 && d.round2Status === 'AllDone');
    
    // 如果 Round 没有任何 AllDone，则该 Round 收益为 0
    if (!r1HasAllDone) {
      round1Value = { meso: 0, coin: 0, tax: 0 };
    }
    if (!r2HasAllDone) {
      round2Value = { meso: 0, coin: 0, tax: 0 };
    }
    
    if (!r1HasAllDone && !r2HasAllDone) {
      messageApi.warning('没有任何 Round 完成（无 AllDone 状态），所有收益为 0');
    } else {
      if (!r1HasAllDone && r1Participants.length > 0) {
        messageApi.info('Round 1 无 AllDone 状态，该 Round 收益为 0');
      }
      if (!r2HasAllDone && r2Participants.length > 0) {
        messageApi.info('Round 2 无 AllDone 状态，该 Round 收益为 0');
      }
    }
    
    // 计算苹果总消耗（用于允许"零收益但需扣苹果"的场景）
    const totalAppleCost = appleEnabled
      ? (appleConsumption || []).reduce((s, a) => s + (parseInt(a.quantity, 10) || 0) * (applePrice || 0), 0)
      : 0;

    if (r1Participants.length === 0 && r2Participants.length === 0 && totalAppleCost === 0) {
      messageApi.warning('没有完成的参与者，无法分配');
      return;
    }

    // 计算角色的累加角色定位奖励（所有角色定位的奖励累加）
    const getTotalRoleBonus = (roles, bossType) => {
      if (!roles || !Array.isArray(roles)) return 0;
      return roles.reduce((sum, role) => sum + getRoleBonusPercent(role, bossType), 0);
    };

    // 判断是否包含"完全不参与分成"的角色定位（bonusPercent <= -100，如 Reg Buyer / AFK Buyer）
    const hasExcludedRole = (roles, bossType) => {
      if (!roles || !Array.isArray(roles)) return false;
      return roles.some(role => getRoleBonusPercent(role, bossType) <= -100);
    };

    // 计算 Round 1 的总比例（只有有 AllDone 时才计算）
    let r1TotalPercent = 0;
    if (r1HasAllDone) {
      r1Participants.forEach(d => {
        if (hasExcludedRole(d.roles, bossType)) return;  // 不参与分成
        const statusRatio = getStatusRatio(d.round1Status, bossType) * 100;
        const roleBonus = getTotalRoleBonus(d.roles, bossType);  // 累加所有角色定位奖励
        r1TotalPercent += Math.max(0, statusRatio + roleBonus);
      });
    }

    // 计算 Round 2 的总比例（只有有 AllDone 时才计算）
    let r2TotalPercent = 0;
    if (r2HasAllDone) {
      r2Participants.forEach(d => {
        if (hasExcludedRole(d.roles, bossType)) return;  // 不参与分成
        const statusRatio = getStatusRatio(d.round2Status, bossType) * 100;
        const roleBonus = getTotalRoleBonus(d.roles, bossType);  // 累加所有角色定位奖励
        r2TotalPercent += Math.max(0, statusRatio + roleBonus);
      });
    }

    // 使用最大余额法分配，确保总额精确相等（支持负数：取绝对值分配后取反）
    const distributeWithLargestRemainder = (total, percentages) => {
      if (percentages.length === 0 || total === 0) return percentages.map(() => 0);

      const totalPercent = percentages.reduce((s, p) => s + p, 0);
      if (totalPercent === 0) return percentages.map(() => 0);

      const isNegative = total < 0;
      const absTotal = Math.abs(total);

      // 计算每个份额的精确值和取整值
      const exactValues = percentages.map(p => (absTotal * p / totalPercent));
      const floorValues = exactValues.map(v => Math.floor(v * 100) / 100);  // 向下取整到分

      // 计算已分配总额和剩余金额
      const distributedTotal = floorValues.reduce((s, v) => s + v, 0);
      let remainder = Math.round((absTotal - distributedTotal) * 100) / 100;  // 剩余金额（精确到分）

      // 计算每个份额的余数，用于分配剩余金额
      const remainders = exactValues.map((v, i) => ({
        index: i,
        remainder: (v * 100) % 1  // 小数部分
      }));

      // 按余数从大到小排序
      remainders.sort((a, b) => b.remainder - a.remainder);

      // 将剩余金额按 0.01 分配给余数最大的份额
      const result = [...floorValues];
      let i = 0;
      while (remainder >= 0.01 && i < remainders.length) {
        result[remainders[i].index] = Math.round((result[remainders[i].index] + 0.01) * 100) / 100;
        remainder = Math.round((remainder - 0.01) * 100) / 100;
        i++;
      }

      return isNegative ? result.map(v => -v) : result;
    };

    // 先计算每个角色的比例
    const distributionData = distributions.map(d => {
      let r1Percent = 0, r2Percent = 0;
      const roleBonus = getTotalRoleBonus(d.roles, bossType);
      const excluded = hasExcludedRole(d.roles, bossType);  // Reg/AFK Buyer 等完全不参与分成的角色

      if (!excluded && r1HasAllDone && d.round1 && d.round1Status && d.round1Status !== 'NotDone' && d.round1Status !== 'Abandoned') {
        const statusRatio = getStatusRatio(d.round1Status, bossType) * 100;
        r1Percent = Math.max(0, statusRatio + roleBonus);
      }

      if (!excluded && r2HasAllDone && d.round2 && d.round2Status && d.round2Status !== 'NotDone' && d.round2Status !== 'Abandoned') {
        const statusRatio = getStatusRatio(d.round2Status, bossType) * 100;
        r2Percent = Math.max(0, statusRatio + roleBonus);
      }

      return { ...d, roleBonus, r1Percent, r2Percent };
    });

    // 使用最大余额法分配 Round 1
    const r1MesoPercents = distributionData.map(d => d.r1Percent);
    const r1CoinPercents = distributionData.map(d => d.r1Percent);
    const r1MesoValues = distributeWithLargestRemainder(round1Value.meso, r1MesoPercents);
    const r1CoinValues = distributeWithLargestRemainder(round1Value.coin, r1CoinPercents);

    // 使用最大余额法分配 Round 2
    const r2MesoPercents = distributionData.map(d => d.r2Percent);
    const r2CoinPercents = distributionData.map(d => d.r2Percent);
    const r2MesoValues = distributeWithLargestRemainder(round2Value.meso, r2MesoPercents);
    const r2CoinValues = distributeWithLargestRemainder(round2Value.coin, r2CoinPercents);

    // 计算每个角色在 R1/R2 分别承担的苹果费用（整数 Meso，用最大余额法确保总和精确）
    const splitAppleCost = (totalCost, weights) => {
      const result = weights.map(() => 0);
      const totalWeight = weights.reduce((s, w) => s + w, 0);
      if (totalCost <= 0 || totalWeight <= 0) return result;
      const exact = weights.map(w => totalCost * w / totalWeight);
      const floors = exact.map(v => Math.floor(v));
      let remainder = totalCost - floors.reduce((s, v) => s + v, 0);
      const order = exact
        .map((v, i) => ({ i, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac);
      for (let k = 0; k < order.length && remainder > 0; k++) {
        floors[order[k].i] += 1;
        remainder -= 1;
      }
      return floors;
    };

    const computeAppleShareForRound = (round, roundCost, shareMode, participantsMask) => {
      const shares = distributionData.map(() => 0);
      if (!appleEnabled || roundCost <= 0) return shares;

      if (shareMode === 'specific') {
        // 按消耗记录里各角色数量承担；不因 Round 状态是否完成而跳过（本 Round 失败也要扣）
        const roundConsumption = (appleConsumption || []).filter(a => a.round === round);
        const price = applePrice || 0;
        roundConsumption.forEach(rec => {
          const qty = parseInt(rec.quantity, 10) || 0;
          if (qty <= 0) return;
          const idx = distributionData.findIndex(d => d.characterName === rec.characterName);
          if (idx < 0) return;
          shares[idx] += qty * price;
        });
      } else {
        // 均分：本 Round 全体报名者（不管完成状态）平摊 roundCost
        const participantIdx = distributionData
          .map((d, i) => ({ d, i }))
          .filter(({ i }) => participantsMask[i]);
        if (participantIdx.length === 0) return shares;
        const weights = participantIdx.map(() => 1);
        const perParticipant = splitAppleCost(roundCost, weights);
        participantIdx.forEach(({ i }, k) => { shares[i] = perParticipant[k]; });
      }
      return shares;
    };

    // 谁在这个 Round 里算"报名参与者"：报名了该 Round 且不是完全排除角色（Reg/AFK Buyer 等）
    // 注意：不再要求状态为 AllDone —— 本 Round 失败也要摊苹果消耗
    const isRoundParticipant = (d, round) => {
      if (hasExcludedRole(d.roles, bossType)) return false;
      if (round === 1) return !!d.round1;
      return !!d.round2;
    };
    const r1Mask = distributionData.map(d => isRoundParticipant(d, 1));
    const r2Mask = distributionData.map(d => isRoundParticipant(d, 2));

    const r1AppleShares = computeAppleShareForRound(1, appleSummary.r1Cost, appleShareModeR1, r1Mask);
    const r2AppleShares = computeAppleShareForRound(2, appleSummary.r2Cost, appleShareModeR2, r2Mask);

    // 从每人 Meso 中扣除各自苹果承担
    const r1MesoNet = r1MesoValues.map((v, i) => Math.round((v - r1AppleShares[i]) * 100) / 100);
    const r2MesoNet = r2MesoValues.map((v, i) => Math.round((v - r2AppleShares[i]) * 100) / 100);

    // 合并分配结果
    const newDistributions = distributionData.map((d, i) => ({
      ...d,
      r1StatusRatio: d.round1Status ? getStatusRatio(d.round1Status, bossType) * 100 : 0,
      r1Meso: r1MesoNet[i],
      r1Coin: r1CoinValues[i],
      r1AppleCost: r1AppleShares[i],
      r2StatusRatio: d.round2Status ? getStatusRatio(d.round2Status, bossType) * 100 : 0,
      r2Meso: r2MesoNet[i],
      r2Coin: r2CoinValues[i],
      r2AppleCost: r2AppleShares[i],
      meso: Math.round((r1MesoNet[i] + r2MesoNet[i]) * 100) / 100,
      coin: Math.round((r1CoinValues[i] + r2CoinValues[i]) * 100) / 100
    }));

    setDistributions(newDistributions);
    if (!skipSave) {
      // 手动计算：更新总额输入并提示。自动重算（已分配 run 还原）时保留历史总额、不弹提示
      setMesoAmount(round1Value.meso + round2Value.meso - appleSummary.totalCost);
      setCoinAmount(round1Value.coin + round2Value.coin);
      messageApi.success(`分配计算完成！R1 总比例: ${r1TotalPercent.toFixed(0)}%, R2 总比例: ${r2TotalPercent.toFixed(0)}%`);
    }
  };

  // 已分配 run 加载后：distributions 就绪时自动重算一次，还原 R1/R2 分列显示
  useEffect(() => {
    if (pendingRecalcRef.current && distributions.length > 0 && runDetails) {
      pendingRecalcRef.current = false;
      calculateDistribution(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributions, runDetails]);

  // 应用自定义罚款：从选中角色本次分配的 meso/coin 中扣除罚款额（并入本次分配，允许扣到负）
  const applyPenalty = () => {
    if (penaltyRows.length === 0) {
      messageApi.warning('请先在下方表格勾选要罚款的角色');
      return;
    }
    const pMeso = Number(penaltyMeso) || 0;
    const pCoin = Number(penaltyCoin) || 0;
    if (pMeso <= 0 && pCoin <= 0) {
      messageApi.warning('请输入罚款的 Meso 或 Coin 数额');
      return;
    }
    const sel = new Set(penaltyRows);
    setDistributions(prev => prev.map(d => {
      if (!sel.has(d.characterName)) return d;
      const newMeso = Math.round(((d.meso || 0) - pMeso) * 100) / 100;
      const newCoin = Math.round(((d.coin || 0) - pCoin) * 100) / 100;
      // 罚款额优先从 R1 收益里体现，方便分列展示一致（仅显示用，不影响总额正确性）
      return {
        ...d,
        meso: newMeso,
        coin: newCoin,
        r1Meso: Math.round(((d.r1Meso || 0) - pMeso) * 100) / 100,
        r1Coin: Math.round(((d.r1Coin || 0) - pCoin) * 100) / 100,
        penaltyMeso: (d.penaltyMeso || 0) + pMeso,
        penaltyCoin: (d.penaltyCoin || 0) + pCoin
      };
    }));
    messageApi.success(`已对 ${penaltyRows.length} 名角色罚款${pMeso > 0 ? ` ${pMeso.toLocaleString()} Meso` : ''}${pCoin > 0 ? ` ${pCoin.toLocaleString()} Coin` : ''}`);
    setPenaltyMeso(0);
    setPenaltyCoin(0);
    setPenaltyRows([]);
  };

  // 保存分配记录（提交待审核）
  const saveDistribution = async () => {
    if (!selectedRun || !distributions.length) {
      messageApi.warning('请先选择运行记录并计算分配');
      return;
    }

    const hasAmount = distributions.some(d => d.meso !== 0 || d.coin !== 0);
    if (!hasAmount) {
      messageApi.warning('请先输入金额并计算分配');
      return;
    }

    setLoading(true);
    try {
      // 只发送必要的字段，确保数值类型正确
      const cleanDistributions = distributions.map(d => ({
        characterName: d.characterName,
        team: parseInt(d.team) || 1,
        round1: !!d.round1,
        round2: !!d.round2,
        roundCount: parseInt(d.roundCount) || 0,
        meso: d.meso || 0,
        coin: d.coin || 0
      }));
      
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/distribute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          runId: selectedRun,
          runName: runDetails?.runName,
          mesoTotal: mesoAmount || 0,
          coinTotal: coinAmount || 0,
          distributions: cleanDistributions,
          createdBy: currentUser?.username
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        messageApi.success(data.message || '分配已提交，等待管理员确认');
        loadHistory();  // 重新加载历史记录以更新分配状态
        // 重置
        setSelectedRun(null);
        setRunDetails(null);
        setDistributions([]);
        setMesoAmount(0);
        setCoinAmount(0);
      } else {
        const data = await res.json();
        // 处理不同类型的错误响应
        let errorMsg = '提交失败';
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail)) {
          errorMsg = data.detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else if (data.detail) {
          errorMsg = JSON.stringify(data.detail);
        }
        messageApi.error(errorMsg);
      }
    } catch (e) {
      messageApi.error('提交失败: ' + e.message);
    }
    setLoading(false);
  };

  // 管理员确认分配
  const approveDistribution = async (runId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/approve-distribution/${runId}?username=${currentUser?.username}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        messageApi.success('分配已确认，收益已划分到各账户');
        loadPendingDistributions();
        loadHistory();
        loadCharacterBalances();
        loadTransactions();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '确认失败');
      }
    } catch (e) {
      messageApi.error('确认失败: ' + e.message);
    }
    setLoading(false);
  };

  // 管理员拒绝分配
  const rejectDistribution = async () => {
    if (!rejectingRunId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/bonus-bank/reject-distribution/${rejectingRunId}?username=${currentUser?.username}&reason=${encodeURIComponent(rejectReason)}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        messageApi.success('分配已拒绝');
        setRejectModalVisible(false);
        setRejectReason('');
        setRejectingRunId(null);
        loadPendingDistributions();
        loadHistory();
      } else {
        const data = await res.json();
        messageApi.error(data.detail || '拒绝失败');
      }
    } catch (e) {
      messageApi.error('拒绝失败: ' + e.message);
    }
    setLoading(false);
  };

  // 打开拒绝弹窗
  const openRejectModal = (runId) => {
    setRejectingRunId(runId);
    setRejectReason('');
    setRejectModalVisible(true);
  };

  // 角色定位标签颜色
  const getRoleColor = (role) => {
    const colors = {
      'regular bishop': 'purple',
      'attacker': 'red',
      'sed bishop': 'magenta',
      'sed target': 'volcano',
      'host': 'gold',
      'sed mule': 'orange',
      'echo mule': 'cyan',
      'in cave echo': 'blue'
    };
    return colors[role] || 'default';
  };

  // 检查是否有 Round 2 参与者
  const hasRound2 = useMemo(() => {
    return distributions.some(d => d.round2);
  }, [distributions]);

  // 分配表格列
  const distributionColumns = useMemo(() => {
    const columns = [
      {
        title: '角色',
        dataIndex: 'characterName',
        key: 'characterName',
        fixed: 'left',
        width: 150,
        render: (name) => (
          <Space>
            <Avatar src={`${API_BASE_URL}/api/getavatar?name=${name}`} size="small" />
            {name}
          </Space>
        )
      },
      { title: '队伍', dataIndex: 'team', key: 'team', width: 70, render: t => <Tag color="blue">T{t}</Tag> },
      { 
        title: '角色定位', 
        dataIndex: 'role', 
        key: 'role', 
        width: 110,
        render: (role, record) => role ? (
          <Space direction="vertical" size={0}>
            <Tag color={getRoleColor(role)} style={{ fontSize: 11 }}>{role}</Tag>
            {record.roleBonus > 0 && <Text type="success" style={{ fontSize: 10 }}>+{record.roleBonus}%</Text>}
          </Space>
        ) : <Tag>-</Tag>
      },
      { 
        title: 'R1 状态', 
        key: 'round1Status', 
        width: 100,
        render: (_, record) => {
          if (!record.round1) return <Tag>-</Tag>;
          return (
            <Space direction="vertical" size={0}>
              <Tag color={record.round1Status === 'AllDone' ? 'green' : record.round1Status === 'Abandoned' ? 'red' : 'blue'}>
                {record.round1Status || 'NotDone'}
              </Tag>
              {record.r1StatusRatio > 0 && <Text type="secondary" style={{ fontSize: 10 }}>{record.r1StatusRatio}%</Text>}
            </Space>
          );
        }
      },
      { 
        title: 'R1 比例', 
        key: 'r1Percent', 
        width: 70,
        render: (_, record) => record.r1Percent > 0 ? <Tag color="purple">{record.r1Percent}%</Tag> : <Tag>-</Tag>
      },
      {
        title: 'R1 收益',
        key: 'r1Income',
        width: 130,
        render: (_, record) => {
          const mesoColor = record.r1Meso > 0 ? '#52c41a' : (record.r1Meso < 0 ? '#ff4d4f' : undefined);
          const coinColor = record.r1Coin > 0 ? '#faad14' : (record.r1Coin < 0 ? '#ff4d4f' : undefined);
          return (
            <Space direction="vertical" size={0}>
              {record.r1Meso !== 0 && <Text style={{ color: mesoColor, fontSize: 12 }}>{Number(record.r1Meso).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Meso</Text>}
              {record.r1Coin !== 0 && <Text style={{ color: coinColor, fontSize: 12 }}>{Number(record.r1Coin).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} C</Text>}
              {record.r1Meso === 0 && record.r1Coin === 0 && <Text type="secondary">-</Text>}
            </Space>
          );
        }
      },
    ];

    // 只有有 Round 2 时才显示 R2 相关列
    if (hasRound2) {
      columns.push(
        { 
          title: 'R2 状态', 
          key: 'round2Status', 
          width: 100,
          render: (_, record) => {
            if (!record.round2) return <Tag>-</Tag>;
            return (
              <Space direction="vertical" size={0}>
                <Tag color={record.round2Status === 'AllDone' ? 'green' : record.round2Status === 'Abandoned' ? 'red' : 'blue'}>
                  {record.round2Status || 'NotDone'}
                </Tag>
                {record.r2StatusRatio > 0 && <Text type="secondary" style={{ fontSize: 10 }}>{record.r2StatusRatio}%</Text>}
              </Space>
            );
          }
        },
        { 
          title: 'R2 比例', 
          key: 'r2Percent', 
          width: 70,
          render: (_, record) => record.r2Percent > 0 ? <Tag color="purple">{record.r2Percent}%</Tag> : <Tag>-</Tag>
        },
        {
          title: 'R2 收益',
          key: 'r2Income',
          width: 130,
          render: (_, record) => {
            const mesoColor = record.r2Meso > 0 ? '#52c41a' : (record.r2Meso < 0 ? '#ff4d4f' : undefined);
            const coinColor = record.r2Coin > 0 ? '#faad14' : (record.r2Coin < 0 ? '#ff4d4f' : undefined);
            return (
              <Space direction="vertical" size={0}>
                {record.r2Meso !== 0 && <Text style={{ color: mesoColor, fontSize: 12 }}>{Number(record.r2Meso).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Meso</Text>}
                {record.r2Coin !== 0 && <Text style={{ color: coinColor, fontSize: 12 }}>{Number(record.r2Coin).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} C</Text>}
                {record.r2Meso === 0 && record.r2Coin === 0 && <Text type="secondary">-</Text>}
              </Space>
            );
          }
        }
      );
    }

    // 总计列
    columns.push(
      {
        title: '总 Meso',
        dataIndex: 'meso',
        key: 'meso',
        width: 120,
        render: v => {
          const n = Number(v || 0);
          const color = n > 0 ? '#52c41a' : (n < 0 ? '#ff4d4f' : undefined);
          return <Text strong style={{ color }}>{n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</Text>;
        }
      },
      {
        title: '总 Coin',
        dataIndex: 'coin',
        key: 'coin',
        width: 90,
        render: v => {
          const n = Number(v || 0);
          const color = n > 0 ? '#faad14' : (n < 0 ? '#ff4d4f' : undefined);
          return <Text strong style={{ color }}>{n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</Text>;
        }
      }
    );

    return columns;
  }, [hasRound2]);

  // 用户余额表格列（主表）
  const balanceColumns = [
    { 
      title: '用户', 
      dataIndex: 'username', 
      key: 'username',
      render: (name, record) => (
        record.isUnassigned 
          ? <Text type="secondary" style={{ fontStyle: 'italic' }}><QuestionCircleOutlined style={{ marginRight: 8 }} />{name}</Text>
          : <Text strong><UserOutlined style={{ marginRight: 8 }} />{name}</Text>
      )
    },
    { title: 'Meso 总额', dataIndex: 'mesoBalance', key: 'mesoBalance', render: v => <Text style={{ color: '#52c41a', fontWeight: 'bold' }}>{(v || 0).toLocaleString()}</Text> },
    { title: 'Coin 总额', dataIndex: 'coinBalance', key: 'coinBalance', render: v => <Text style={{ color: '#faad14', fontWeight: 'bold' }}>{(v || 0).toLocaleString()}</Text> },
  ];

  // 角色余额展开列（子表）
  const characterBalanceColumns = [
    { 
      title: '角色', 
      dataIndex: 'characterName', 
      key: 'characterName',
      render: (name) => (
        <Space>
          <Avatar src={`${API_BASE_URL}/api/getavatar?name=${name}`} size="small" />
          {name}
        </Space>
      )
    },
    { title: 'Meso', dataIndex: 'mesoBalance', key: 'mesoBalance', render: v => <Text style={{ color: '#52c41a' }}>{(v || 0).toLocaleString()}</Text> },
    { title: 'Coin', dataIndex: 'coinBalance', key: 'coinBalance', render: v => <Text style={{ color: '#faad14' }}>{(v || 0).toLocaleString()}</Text> },
  ];

  // 展开行渲染
  const expandedRowRender = (record) => {
    return (
      <Table
        columns={characterBalanceColumns}
        dataSource={record.characters || []}
        rowKey="characterName"
        pagination={false}
        size="small"
        showHeader={false}
        style={{ marginLeft: 20 }}
      />
    );
  };

  // 交易记录列（按用户显示）
  const transactionColumns = [
    { title: '时间', dataIndex: 'createdAt', key: 'createdAt', render: t => new Date(t).toLocaleString(), width: 160 },
    { 
      title: '类型', 
      dataIndex: 'type', 
      key: 'type', 
      width: 80,
      render: (type) => type === 'withdrawal' ? 
        <Tag color="orange">提取</Tag> : 
        <Tag color="green">分配</Tag>
    },
    { title: '来源', dataIndex: 'runName', key: 'runName', width: 150, ellipsis: true },
    { 
      title: '用户', 
      dataIndex: 'username', 
      key: 'username',
      width: 120,
      render: (name) => <Text strong><UserOutlined style={{ marginRight: 4 }} />{name}</Text>
    },
    { 
      title: 'Meso', 
      dataIndex: 'meso', 
      key: 'meso', 
      width: 130,
      render: (v, record) => {
        const val = v || 0;
        const isNegative = val < 0;
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ color: isNegative ? '#ff4d4f' : '#52c41a' }}>
              {isNegative ? '' : '+'}{val.toLocaleString()}
            </Text>
            {record.mesoFee > 0 && <Text type="secondary" style={{ fontSize: 10 }}>手续费: -{record.mesoFee}</Text>}
          </Space>
        );
      }
    },
    { 
      title: 'Coin', 
      dataIndex: 'coin', 
      key: 'coin', 
      width: 80,
      render: v => {
        const val = v || 0;
        const isNegative = val < 0;
        return <Text style={{ color: isNegative ? '#ff4d4f' : '#faad14' }}>
          {isNegative ? '' : '+'}{val.toLocaleString()}
        </Text>;
      }
    },
  ];

  // 交易记录按日期分组（折叠用）
  const groupedTransactions = useMemo(() => {
    const map = {};
    for (const t of transactions) {
      const date = formatDate(t.createdAt);
      (map[date] = map[date] || []).push(t);
    }
    return Object.keys(map).sort().reverse().map(date => ({ date, items: map[date] }));
  }, [transactions]);

  // 检查运行是否已分配（从历史记录中获取）
  const isRunDistributed = useCallback((runId) => {
    const run = historyList.find(h => h.runId === runId);
    return run?.distributed || false;
  }, [historyList]);

  // 当前选中的 Run 是否已分配
  const isCurrentRunDistributed = useMemo(() => {
    return selectedRun ? isRunDistributed(selectedRun) : false;
  }, [selectedRun, isRunDistributed]);

  // 是否可以编辑（Host 或管理员权限 + 未分配）
  // 注意：如果 runDetails 存在但 hostUsernames 为空，说明加载掉落数据失败或 Run 没有 Host
  // 只有在确认是 Host、管理员或 runDetails 尚未加载时才允许编辑
  const canEdit = useMemo(() => {
    if (isCurrentRunDistributed) return false;
    if (!runDetails) return false; // 未选择 Run 时不能编辑
    if (runDetails.distributionPending) return false; // 待确认时不能编辑
    // 管理员可以编辑
    if (isAdmin) return true;
    // 如果有 Host 信息，检查是否是 Host；否则检查 runDetails 中的 characters 是否有 Host
    if (hostUsernames.length > 0) {
      return isHost;
    }
    // 兜底：如果 hostUsernames 为空但 runDetails 有数据，检查是否真的没有 Host
    const hostsInRun = (runDetails.characters || []).filter(c => c.roles?.includes('host'));
    if (hostsInRun.length === 0) {
      return true; // 真的没有 Host，允许任何人编辑
    }
    return false; // 有 Host 但没有匹配到当前用户，不允许编辑
  }, [isCurrentRunDistributed, runDetails, hostUsernames.length, isHost, isAdmin]);

  // 添加掉落物品 (支持分 Round，包含售卖方式)
  const handleAddLoot = (round, itemName, quantity = 1, sellMethod = 'NPC') => {
    const setLoot = round === 1 ? setRound1Loot : setRound2Loot;
    // 获取物品配置价值
    const itemConfig = lootConfig.items?.find(i => i.name === itemName);
    const defaultMeso = itemConfig?.mesoValue || 0;
    const defaultCoin = itemConfig?.coinValue || 0;
    
    setLoot(prev => {
      const items = [...(prev.items || [])];
      const existing = items.find(l => l.name === itemName);
      if (existing) {
        return { ...prev, items: items.map(l => l.name === itemName ? { ...l, quantity: l.quantity + quantity } : l) };
      }
      return { 
        ...prev, 
        items: [...items, { 
          name: itemName, 
          quantity, 
          sellMethod,  // FM/NPC/Others
          customMeso: sellMethod === 'NPC' ? null : defaultMeso,  // 自定义价格
          customCoin: sellMethod === 'NPC' ? null : defaultCoin
        }] 
      };
    });
  };

  // 更新物品售卖方式
  const handleUpdateLootSellMethod = (round, itemName, sellMethod, customMeso, customCoin, currencyType) => {
    const setLoot = round === 1 ? setRound1Loot : setRound2Loot;
    setLoot(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.name === itemName 
          ? { 
              ...item, 
              sellMethod, 
              customMeso: sellMethod === 'NPC' ? null : (customMeso !== undefined ? customMeso : (item.customMeso ?? 0)),
              customCoin: sellMethod === 'NPC' ? null : (customCoin !== undefined ? customCoin : (item.customCoin ?? 0)),
              currencyType: sellMethod === 'NPC' ? null : (currencyType || item.currencyType || 'meso')
            } 
          : item
      )
    }));
  };

  // 删除掉落物品 (支持分 Round)
  const handleDeleteLoot = (round, itemName) => {
    const setLoot = round === 1 ? setRound1Loot : setRound2Loot;
    setLoot(prev => ({ ...prev, items: (prev.items || []).filter(l => l.name !== itemName) }));
  };

  // 添加服务收入 (支持分 Round)
  const handleAddService = (round, serviceName, meso, coin) => {
    const setLoot = round === 1 ? setRound1Loot : setRound2Loot;
    setLoot(prev => ({
      ...prev,
      services: [...(prev.services || []), { name: serviceName, meso: meso || 0, coin: coin || 0 }]
    }));
  };

  // 删除服务收入 (支持分 Round)
  const handleDeleteService = (round, index) => {
    const setLoot = round === 1 ? setRound1Loot : setRound2Loot;
    setLoot(prev => ({ ...prev, services: (prev.services || []).filter((_, i) => i !== index) }));
  };

  // 计算各 Round 的预估价值
  const round1Summary = useMemo(() => calculateRoundLootValue(round1Loot), [round1Loot, calculateRoundLootValue]);
  const round2Summary = useMemo(() => calculateRoundLootValue(round2Loot), [round2Loot, calculateRoundLootValue]);

  // 苹果消耗汇总（按 Round 分）
  const appleSummary = useMemo(() => {
    if (!appleEnabled) {
      return { r1Quantity: 0, r2Quantity: 0, r1Cost: 0, r2Cost: 0, totalCost: 0 };
    }
    const r1Quantity = (appleConsumption || [])
      .filter(a => a.round === 1)
      .reduce((s, a) => s + (parseInt(a.quantity, 10) || 0), 0);
    const r2Quantity = (appleConsumption || [])
      .filter(a => a.round === 2)
      .reduce((s, a) => s + (parseInt(a.quantity, 10) || 0), 0);
    const price = applePrice || 0;
    return {
      r1Quantity,
      r2Quantity,
      r1Cost: r1Quantity * price,
      r2Cost: r2Quantity * price,
      totalCost: (r1Quantity + r2Quantity) * price
    };
  }, [appleEnabled, appleConsumption, applePrice]);

  // 未分配时，Meso 总额 = 掉落总额 - 苹果消耗（允许为负，用于反向扣除）
  useEffect(() => {
    if (!runDetails) return;
    if (runDetails.distributed) return; // 已分配不修改
    const gross = (round1Summary.meso || 0) + (round2Summary.meso || 0);
    setMesoAmount(gross - (appleSummary.totalCost || 0));
    setCoinAmount((round1Summary.coin || 0) + (round2Summary.coin || 0));
  }, [runDetails, round1Summary.meso, round1Summary.coin, round2Summary.meso, round2Summary.coin, appleSummary.totalCost]);

  // 根据当前选中 Run 的 Boss 类型过滤物品
  const filteredLootItems = useMemo(() => {
    if (!runDetails) return lootConfig.items || [];
    const bossType = runDetails.bossType || 'HT';
    return (lootConfig.items || []).filter(item => !item.bossType || item.bossType === bossType);
  }, [lootConfig.items, runDetails]);

  return (
    <div>
      {contextHolder}
      
      {/* 掉落物品管理 - 只有选中记录时显示，分 Round 管理 */}
      {runDetails && (
        <Card 
          title={
            <Space>
              <GiftOutlined /> 掉落物品与服务收入
              {isCurrentRunDistributed && (
                <Tag color="green">已分配 - 只读</Tag>
              )}
              {!isCurrentRunDistributed && !isHost && !isAdmin && hostUsernames.length > 0 && (
                <Tag color="orange">只读 - 只有 Host 或管理员可编辑</Tag>
              )}
              {!isCurrentRunDistributed && isAdmin && !isHost && (
                <Tag color="purple">管理员模式</Tag>
              )}
              {!isCurrentRunDistributed && hostUsernames.length === 0 && (
                <Tag color="blue">无 Host 限制</Tag>
              )}
            </Space>
          } 
          style={{ marginBottom: 16 }}
          extra={
            <Button 
              type="primary" 
              icon={<SaveOutlined />} 
              onClick={saveLootData}
              loading={loading}
              disabled={!canEdit}
            >
              保存掉落数据
            </Button>
          }
        >
          <Row gutter={16}>
            {/* Round 1 掉落物品 */}
            <Col span={hasRound2 ? 12 : 24}>
              <Card 
                size="small" 
                title={<><Tag color="blue">Round 1</Tag> 掉落物品与服务</>} 
                type="inner"
                extra={<Text type="secondary">总计: {round1Summary.meso.toLocaleString()} Meso / {round1Summary.coin} Coin</Text>}
              >
                {/* R1 添加物品 */}
                {canEdit && (
                  <Form layout="inline" size="small" style={{ marginBottom: 8 }} onFinish={(v) => { if (v.r1Item) { handleAddLoot(1, v.r1Item, v.r1Qty || 1); } }}>
                    <Form.Item name="r1Item" style={{ marginBottom: 0 }}>
                      <Select placeholder="选择物品" style={{ width: 140 }} allowClear>
                        {filteredLootItems.map(item => (
                          <Option key={item.name} value={item.name}>{item.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="r1Qty" initialValue={1} style={{ marginBottom: 0 }}>
                      <InputNumber min={1} max={99} style={{ width: 60 }} placeholder="数量" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>添加</Button>
                    </Form.Item>
                  </Form>
                )}
                <Table
                  dataSource={round1Loot.items || []}
                  rowKey="name"
                  size="small"
                  pagination={false}
                  locale={{ emptyText: '无掉落' }}
                  columns={[
                    { title: '物品', dataIndex: 'name', ellipsis: true, width: 120, render: (name) => {
                      const icon = getItemIcon(name);
                      return icon
                        ? <Space size={6}><img src={icon} alt={name} style={{ width: 20, height: 20, objectFit: 'contain', verticalAlign: 'middle' }} />{name}</Space>
                        : name;
                    } },
                    { title: '数量', dataIndex: 'quantity', width: 60, render: (v, record) => (
                      canEdit ? (
                        <InputNumber 
                          size="small" 
                          min={1} 
                          max={99} 
                          value={v} 
                          style={{ width: 50 }}
                          onChange={(newVal) => {
                            if (newVal && newVal > 0) {
                              setRound1Loot(prev => ({
                                ...prev,
                                items: prev.items.map(item => 
                                  item.name === record.name ? { ...item, quantity: newVal } : item
                                )
                              }));
                            }
                          }}
                        />
                      ) : <Tag>{v}</Tag>
                    )},
                    { title: '售卖', dataIndex: 'sellMethod', width: 80, render: (v, record) => (
                      canEdit ? (
                        <Select 
                          size="small" 
                          value={v || 'NPC'} 
                          style={{ width: 70 }}
                          onChange={(val) => handleUpdateLootSellMethod(1, record.name, val)}
                        >
                          <Option value="NPC">NPC</Option>
                          <Option value="FM">FM</Option>
                          <Option value="Others">其他</Option>
                        </Select>
                      ) : <Tag>{v || 'NPC'}</Tag>
                    )},
                    { title: '价值', width: 120, render: (_, r) => {
                      const itemConfig = lootConfig.items.find(i => i.name === r.name);
                      const sellMethod = r.sellMethod || 'NPC';
                      const qty = r.quantity || 1;
                      
                      if (sellMethod === 'NPC') {
                        // NPC: 显示配置价值
                        if (!itemConfig) return '-';
                        const val = itemConfig.coinValue > 0 ? `${itemConfig.coinValue * qty} Coin` : `${(itemConfig.mesoValue * qty).toLocaleString()} Meso`;
                        return <Text style={{ color: itemConfig.coinValue > 0 ? '#faad14' : '#52c41a' }}>{val}</Text>;
                      } else {
                        // FM/Others: 允许自定义输入（单个输入框 + 货币选择）
                        const currencyType = r.currencyType || (r.customCoin > 0 ? 'coin' : 'meso');
                        const customValue = currencyType === 'coin' ? (r.customCoin || 0) : (r.customMeso || 0);
                        
                        if (canEdit) {
                          return (
                            <Space.Compact size="small">
                              <InputNumber 
                                size="small" 
                                min={0} 
                                value={customValue}
                                style={{ width: 100 }}
                                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={v => v.replace(/,/g, '')}
                                onChange={(val) => {
                                  if (currencyType === 'coin') {
                                    handleUpdateLootSellMethod(1, r.name, sellMethod, 0, val, 'coin');
                                  } else {
                                    handleUpdateLootSellMethod(1, r.name, sellMethod, val, 0, 'meso');
                                  }
                                }}
                              />
                              <Select 
                                size="small" 
                                value={currencyType}
                                style={{ width: 75 }}
                                onChange={(val) => {
                                  if (val === 'coin') {
                                    handleUpdateLootSellMethod(1, r.name, sellMethod, 0, customValue || 0, 'coin');
                                  } else {
                                    handleUpdateLootSellMethod(1, r.name, sellMethod, customValue || 0, 0, 'meso');
                                  }
                                }}
                              >
                                <Option value="meso">Meso</Option>
                                <Option value="coin">Coin</Option>
                              </Select>
                            </Space.Compact>
                          );
                        }
                        const rawMeso = (r.customMeso || 0) * qty;
                        const mesoAfterTax = rawMeso > 0 ? Math.round(rawMeso * 0.975) : 0;
                        const coin = (r.customCoin || 0) * qty;
                        if (coin > 0) {
                          return <Text style={{ color: '#faad14' }}>{coin} Coin</Text>;
                        }
                        return (
                          <Space direction="vertical" size={0}>
                            <Text style={{ color: '#52c41a' }}>{mesoAfterTax.toLocaleString()} Meso</Text>
                            <Text type="secondary" style={{ fontSize: 10 }}>(-2.5%税)</Text>
                          </Space>
                        );
                      }
                    }},
                    { title: '', width: 30, render: (_, r) => canEdit ? <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteLoot(1, r.name)} /> : null }
                  ]}
                />
                <Divider style={{ margin: '8px 0' }} />
                {/* R1 添加服务 */}
                {canEdit && (
                  <Form form={addLootForm} layout="inline" onFinish={(v) => { handleAddService(1, v.svc, v.m, v.c); addLootForm.resetFields(); }} size="small">
                    <Form.Item name="svc" rules={[{ required: true }]}><Select placeholder="服务" style={{ width: 110 }}>{(lootConfig.serviceTypes || []).map(s => <Option key={s.name} value={s.name}>{s.name}</Option>)}<Option value="Other">其他</Option></Select></Form.Item>
                    <Form.Item name="m"><InputNumber placeholder="Meso" min={0} step={0.01} style={{ width: 120 }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={v => v.replace(/,/g, '')} /></Form.Item>
                    <Form.Item name="c"><InputNumber placeholder="Coin" min={0} step={0.01} style={{ width: 80 }} /></Form.Item>
                    <Form.Item><Button type="primary" htmlType="submit" size="small">添加</Button></Form.Item>
                  </Form>
                )}
                <Table
                  dataSource={(round1Loot.services || []).map((s, i) => ({ ...s, key: i }))}
                  rowKey="key"
                  size="small"
                  pagination={false}
                  locale={{ emptyText: '无服务收入' }}
                  style={{ marginTop: 8 }}
                  columns={[
                    { title: '服务', dataIndex: 'name' },
                    { title: 'Meso', dataIndex: 'meso', render: v => v > 0 ? <Text style={{ color: '#52c41a' }}>{v.toLocaleString()}</Text> : '-' },
                    { title: 'Coin', dataIndex: 'coin', render: v => v > 0 ? <Text style={{ color: '#faad14' }}>{v}</Text> : '-' },
                    { title: '', width: 30, render: (_, __, i) => canEdit ? <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteService(1, i)} /> : null }
                  ]}
                />
              </Card>
            </Col>

            {/* Round 2 掉落物品 - 只有有 R2 参与者时显示 */}
            {hasRound2 && (
              <Col span={12}>
                <Card 
                  size="small" 
                  title={<><Tag color="purple">Round 2</Tag> 掉落物品与服务</>} 
                  type="inner"
                  extra={<Text type="secondary">总计: {round2Summary.meso.toLocaleString()} Meso / {round2Summary.coin} Coin</Text>}
                >
                  {/* R2 添加物品 */}
                  {canEdit && (
                    <Form layout="inline" size="small" style={{ marginBottom: 8 }} onFinish={(v) => { if (v.r2Item) { handleAddLoot(2, v.r2Item, v.r2Qty || 1); } }}>
                      <Form.Item name="r2Item" style={{ marginBottom: 0 }}>
                        <Select placeholder="选择物品" style={{ width: 140 }} allowClear>
                          {filteredLootItems.map(item => (
                            <Option key={item.name} value={item.name}>{item.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item name="r2Qty" initialValue={1} style={{ marginBottom: 0 }}>
                        <InputNumber min={1} max={99} style={{ width: 60 }} placeholder="数量" />
                      </Form.Item>
                      <Form.Item style={{ marginBottom: 0 }}>
                        <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>添加</Button>
                      </Form.Item>
                    </Form>
                  )}
                  <Table
                    dataSource={round2Loot.items || []}
                    rowKey="name"
                    size="small"
                    pagination={false}
                    locale={{ emptyText: '无掉落' }}
                    columns={[
                      { title: '物品', dataIndex: 'name', ellipsis: true, width: 120, render: (name) => {
                        const icon = getItemIcon(name);
                        return icon
                          ? <Space size={6}><img src={icon} alt={name} style={{ width: 20, height: 20, objectFit: 'contain', verticalAlign: 'middle' }} />{name}</Space>
                          : name;
                      } },
                      { title: '数量', dataIndex: 'quantity', width: 60, render: (v, record) => (
                        canEdit ? (
                          <InputNumber 
                            size="small" 
                            min={1} 
                            max={99} 
                            value={v} 
                            style={{ width: 50 }}
                            onChange={(newVal) => {
                              if (newVal && newVal > 0) {
                                setRound2Loot(prev => ({
                                  ...prev,
                                  items: prev.items.map(item => 
                                    item.name === record.name ? { ...item, quantity: newVal } : item
                                  )
                                }));
                              }
                            }}
                          />
                        ) : <Tag>{v}</Tag>
                      )},
                      { title: '售卖', dataIndex: 'sellMethod', width: 80, render: (v, record) => (
                        canEdit ? (
                          <Select 
                            size="small" 
                            value={v || 'NPC'} 
                            style={{ width: 70 }}
                            onChange={(val) => handleUpdateLootSellMethod(2, record.name, val)}
                          >
                            <Option value="NPC">NPC</Option>
                            <Option value="FM">FM</Option>
                            <Option value="Others">其他</Option>
                          </Select>
                        ) : <Tag>{v || 'NPC'}</Tag>
                      )},
                      { title: '价值', width: 120, render: (_, r) => {
                        const itemConfig = lootConfig.items.find(i => i.name === r.name);
                        const sellMethod = r.sellMethod || 'NPC';
                        const qty = r.quantity || 1;
                        
                        if (sellMethod === 'NPC') {
                          if (!itemConfig) return '-';
                          const val = itemConfig.coinValue > 0 ? `${itemConfig.coinValue * qty} Coin` : `${(itemConfig.mesoValue * qty).toLocaleString()} Meso`;
                          return <Text style={{ color: itemConfig.coinValue > 0 ? '#faad14' : '#52c41a' }}>{val}</Text>;
                        } else {
                          // FM/Others: 允许自定义输入（单个输入框 + 货币选择）
                          const currencyType = r.currencyType || (r.customCoin > 0 ? 'coin' : 'meso');
                          const customValue = currencyType === 'coin' ? (r.customCoin || 0) : (r.customMeso || 0);
                          
                          if (canEdit) {
                            return (
                              <Space.Compact size="small">
                                <InputNumber 
                                  size="small" 
                                  min={0} 
                                  value={customValue}
                                  style={{ width: 100 }}
                                  formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                  parser={v => v.replace(/,/g, '')}
                                  onChange={(val) => {
                                    if (currencyType === 'coin') {
                                      handleUpdateLootSellMethod(2, r.name, sellMethod, 0, val, 'coin');
                                    } else {
                                      handleUpdateLootSellMethod(2, r.name, sellMethod, val, 0, 'meso');
                                    }
                                  }}
                                />
                                <Select 
                                  size="small" 
                                  value={currencyType}
                                  style={{ width: 75 }}
                                  onChange={(val) => {
                                    if (val === 'coin') {
                                      handleUpdateLootSellMethod(2, r.name, sellMethod, 0, customValue || 0, 'coin');
                                    } else {
                                      handleUpdateLootSellMethod(2, r.name, sellMethod, customValue || 0, 0, 'meso');
                                    }
                                  }}
                                >
                                  <Option value="meso">Meso</Option>
                                  <Option value="coin">Coin</Option>
                                </Select>
                              </Space.Compact>
                            );
                          }
                          const rawMeso = (r.customMeso || 0) * qty;
                          const mesoAfterTax = rawMeso > 0 ? Math.round(rawMeso * 0.975) : 0;
                          const coin = (r.customCoin || 0) * qty;
                          if (coin > 0) {
                            return <Text style={{ color: '#faad14' }}>{coin} Coin</Text>;
                          }
                          return (
                            <Space direction="vertical" size={0}>
                              <Text style={{ color: '#52c41a' }}>{mesoAfterTax.toLocaleString()} Meso</Text>
                              <Text type="secondary" style={{ fontSize: 10 }}>(-2.5%税)</Text>
                            </Space>
                          );
                        }
                      }},
                      { title: '', width: 30, render: (_, r) => canEdit ? <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteLoot(2, r.name)} /> : null }
                    ]}
                  />
                  <Divider style={{ margin: '8px 0' }} />
                  {/* R2 添加服务 */}
                  {canEdit && (
                    <Form form={addServiceForm} layout="inline" onFinish={(v) => { handleAddService(2, v.svc2, v.m2, v.c2); addServiceForm.resetFields(); }} size="small">
                      <Form.Item name="svc2" rules={[{ required: true }]}><Select placeholder="服务" style={{ width: 110 }}>{(lootConfig.serviceTypes || []).map(s => <Option key={s.name} value={s.name}>{s.name}</Option>)}<Option value="Other">其他</Option></Select></Form.Item>
                      <Form.Item name="m2"><InputNumber placeholder="Meso" min={0} step={0.01} style={{ width: 120 }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={v => v.replace(/,/g, '')} /></Form.Item>
                      <Form.Item name="c2"><InputNumber placeholder="Coin" min={0} step={0.01} style={{ width: 80 }} /></Form.Item>
                      <Form.Item><Button type="primary" htmlType="submit" size="small">添加</Button></Form.Item>
                    </Form>
                  )}
                  <Table
                    dataSource={(round2Loot.services || []).map((s, i) => ({ ...s, key: i }))}
                    rowKey="key"
                    size="small"
                    pagination={false}
                    locale={{ emptyText: '无服务收入' }}
                    style={{ marginTop: 8 }}
                    columns={[
                      { title: '服务', dataIndex: 'name' },
                      { title: 'Meso', dataIndex: 'meso', render: v => v > 0 ? <Text style={{ color: '#52c41a' }}>{v.toLocaleString()}</Text> : '-' },
                      { title: 'Coin', dataIndex: 'coin', render: v => v > 0 ? <Text style={{ color: '#faad14' }}>{v}</Text> : '-' },
                      { title: '', width: 30, render: (_, __, i) => canEdit ? <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteService(2, i)} /> : null }
                    ]}
                  />
                </Card>
              </Col>
            )}
          </Row>
          
          {/* 汇总 */}
          <Divider />
          <Row gutter={24} justify="center">
            <Col><Statistic title="R1 Meso" value={round1Summary.meso} valueStyle={{ color: '#52c41a' }} formatter={v => v.toLocaleString()} /></Col>
            <Col><Statistic title="R1 Coin" value={round1Summary.coin} valueStyle={{ color: '#faad14' }} /></Col>
            {hasRound2 && <Col><Statistic title="R2 Meso" value={round2Summary.meso} valueStyle={{ color: '#52c41a' }} formatter={v => v.toLocaleString()} /></Col>}
            {hasRound2 && <Col><Statistic title="R2 Coin" value={round2Summary.coin} valueStyle={{ color: '#faad14' }} /></Col>}
            <Col><Statistic title="总 Meso" value={round1Summary.meso + round2Summary.meso} valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} prefix={<DollarOutlined />} formatter={v => v.toLocaleString()} /></Col>
            <Col><Statistic title="总 Coin" value={round1Summary.coin + round2Summary.coin} valueStyle={{ color: '#faad14', fontWeight: 'bold' }} prefix={<GoldOutlined />} /></Col>
            {(round1Summary.tax > 0 || round2Summary.tax > 0) && (
              <Col>
                <Statistic
                  title="交易税 (2.5%)"
                  value={(round1Summary.tax || 0) + (round2Summary.tax || 0)}
                  valueStyle={{ color: '#ff4d4f' }}
                  formatter={v => `-${v.toLocaleString()}`}
                />
              </Col>
            )}
          </Row>
        </Card>
      )}

      {/* 苹果消耗分配 - 只有加载 Run 后且尚未分配时显示 */}
      {runDetails && !isCurrentRunDistributed && appleEnabled && (
        <Card
          title={
            <Space>
              <img src="/apple.png" alt="apple" style={{ width: 20, height: 20, verticalAlign: 'middle' }} />
              苹果消耗分配
              {!isHost && !isAdmin && hostUsernames.length > 0 && (
                <Tag color="orange">只读 - 只有 Host 或管理员可编辑</Tag>
              )}
            </Space>
          }
          style={{ marginBottom: 16 }}
          extra={
            <Space>
              <Text>单个苹果价值：</Text>
              <InputNumber
                min={0}
                step={100000}
                value={applePrice}
                onChange={v => setApplePrice(v || 0)}
                style={{ width: 160 }}
                formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={v => v.replace(/,/g, '')}
                disabled={!canEdit}
              />
              <Text type="secondary">Meso</Text>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={saveAppleData}
                loading={loading}
                disabled={!canEdit}
              >
                保存苹果数据
              </Button>
            </Space>
          }
        >
          <Row gutter={16}>
            {[1, 2].filter(round => round === 1 || hasRound2).map(round => {
              const shareMode = round === 1 ? appleShareModeR1 : appleShareModeR2;
              const setShareMode = round === 1 ? setAppleShareModeR1 : setAppleShareModeR2;
              const roundCost = round === 1 ? appleSummary.r1Cost : appleSummary.r2Cost;
              const roundQty = round === 1 ? appleSummary.r1Quantity : appleSummary.r2Quantity;
              const roundConsumption = appleConsumption.filter(a => a.round === round);
              // 本 Round 参与者：参加了且不是全 Abandoned 的 NotDone/Abandoned 也视为未参与
              const roundParticipants = (runDetails?.characters || []).filter(c => {
                if (round === 1) return c.round1;
                return c.round2;
              });
              const roundColor = round === 1 ? 'blue' : 'purple';

              return (
                <Col span={hasRound2 ? 12 : 24} key={round}>
                  <Card
                    size="small"
                    type="inner"
                    title={
                      <Space>
                        <Tag color={roundColor}>Round {round}</Tag>
                        苹果消耗
                      </Space>
                    }
                    extra={
                      <Text type="secondary">
                        {roundQty} 个 / <Text style={{ color: '#ff4d4f' }}>-{roundCost.toLocaleString()}</Text> Meso
                      </Text>
                    }
                    style={{ marginBottom: 8 }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <Text style={{ marginRight: 8 }}>分摊方式：</Text>
                      <Radio.Group
                        size="small"
                        value={shareMode}
                        onChange={e => setShareMode(e.target.value)}
                        disabled={!canEdit}
                      >
                        <Radio.Button value="even">本 Round 全员均分</Radio.Button>
                        <Radio.Button value="specific">特定人员承担</Radio.Button>
                      </Radio.Group>
                    </div>

                    {canEdit && (
                      <Form
                        layout="inline"
                        size="small"
                        style={{ marginBottom: 8 }}
                        onFinish={(v) => {
                          const chars = Array.isArray(v.appleChar) ? v.appleChar : (v.appleChar ? [v.appleChar] : []);
                          const qty = parseInt(v.appleQty, 10) || 0;
                          if (chars.length === 0 || qty < 1) return;
                          setAppleConsumption(prev => {
                            let next = [...prev];
                            chars.forEach(name => {
                              const idx = next.findIndex(a => a.round === round && a.characterName === name);
                              if (idx >= 0) {
                                next[idx] = { ...next[idx], quantity: (parseInt(next[idx].quantity, 10) || 0) + qty };
                              } else {
                                next.push({ round, characterName: name, quantity: qty });
                              }
                            });
                            return next;
                          });
                        }}
                      >
                        <Form.Item name="appleChar" rules={[{ required: true, message: '请选择角色' }]}>
                          <Select
                            mode="multiple"
                            allowClear
                            placeholder={`选择 R${round} 参与角色（可多选）`}
                            style={{ minWidth: 260 }}
                            showSearch
                            optionFilterProp="children"
                            maxTagCount="responsive"
                          >
                            {roundParticipants.map(c => (
                              <Option key={c.name} value={c.name}>{c.name}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item name="appleQty" initialValue={1} rules={[{ required: true }]}>
                          <InputNumber min={1} step={1} precision={0} style={{ width: 90 }} placeholder="每人数量" />
                        </Form.Item>
                        <Form.Item>
                          <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>添加</Button>
                        </Form.Item>
                      </Form>
                    )}

                    <Table
                      dataSource={roundConsumption.map((a, i) => ({ ...a, key: `${round}-${a.characterName}-${i}` }))}
                      rowKey="key"
                      size="small"
                      pagination={false}
                      locale={{ emptyText: '暂无苹果消耗记录' }}
                      columns={[
                        {
                          title: '角色',
                          dataIndex: 'characterName',
                          render: (name) => (
                            <Space>
                              <Avatar src={`${API_BASE_URL}/api/getavatar?name=${name}`} size="small" />
                              {name}
                            </Space>
                          )
                        },
                        {
                          title: '苹果数量',
                          dataIndex: 'quantity',
                          width: 120,
                          render: (v, record) => canEdit ? (
                            <InputNumber
                              size="small"
                              min={1}
                              step={1}
                              precision={0}
                              value={v}
                              style={{ width: 80 }}
                              onChange={(newVal) => {
                                if (newVal && newVal > 0) {
                                  setAppleConsumption(prev => prev.map(a =>
                                    a.round === round && a.characterName === record.characterName
                                      ? { ...a, quantity: parseInt(newVal, 10) || 0 }
                                      : a
                                  ));
                                }
                              }}
                            />
                          ) : <Tag color="red">{v}</Tag>
                        },
                        {
                          title: '消耗价值 (Meso)',
                          width: 160,
                          render: (_, record) => {
                            const cost = (parseInt(record.quantity, 10) || 0) * (applePrice || 0);
                            return <Text style={{ color: '#ff4d4f' }}>-{cost.toLocaleString()}</Text>;
                          }
                        },
                        {
                          title: '',
                          width: 40,
                          render: (_, record) => canEdit ? (
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => setAppleConsumption(prev => prev.filter(a => !(a.round === round && a.characterName === record.characterName)))}
                            />
                          ) : null
                        }
                      ]}
                      summary={() => roundConsumption.length > 0 ? (
                        <Table.Summary.Row style={{ background: '#fff1f0', fontWeight: 'bold' }}>
                          <Table.Summary.Cell index={0}>合计</Table.Summary.Cell>
                          <Table.Summary.Cell index={1}>
                            <Tag color="red">{roundQty} 个</Tag>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2}>
                            <Text strong style={{ color: '#ff4d4f' }}>-{roundCost.toLocaleString()}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3}></Table.Summary.Cell>
                        </Table.Summary.Row>
                      ) : null}
                    />
                    {shareMode === 'even' && (
                      <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                        当前模式：本 Round 苹果总费用将由该 Round 全体参与者均摊。上表仅作记账。
                      </Text>
                    )}
                    {shareMode === 'specific' && (
                      <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
                        当前模式：每位角色按上表数量各自承担对应 Meso 费用。
                      </Text>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}

      {/* 分配计算区域 */}
      <Card title={<><CalculatorOutlined /> 收益分配</>} style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Row gutter={16} align="middle">
            <Col>
              <Text>选择运行记录：</Text>
              <Select
                placeholder="选择历史运行记录"
                style={{ width: 400, marginLeft: 8 }}
                value={selectedRun}
                onChange={handleSelectRun}
                allowClear
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {historyLimit > 0 && (
                      <div style={{ padding: '8px', textAlign: 'center', borderTop: '1px solid #f0f0f0' }}>
                        <Button type="link" size="small" onClick={loadAllHistory}>
                          加载全部历史记录
                        </Button>
                      </div>
                    )}
                  </>
                )}
              >
                {historyList.map(h => {
                  const distributed = isRunDistributed(h.runId);
                  const isPending = h.distributionPending;
                  const isRejected = h.distributionRejected;
                  return (
                    <Option key={h.runId} value={h.runId}>
                      {distributed && <Tag color="green" style={{ marginRight: 4 }}>已分配</Tag>}
                      {isPending && <Tag color="orange" style={{ marginRight: 4 }}><ClockCircleOutlined /> 待确认</Tag>}
                      {isRejected && <Tag color="red" style={{ marginRight: 4 }}>已拒绝</Tag>}
                      {h.runName} ({h.characterCount}人) - {new Date(h.completedAt).toLocaleDateString()}
                    </Option>
                  );
                })}
              </Select>
              {historyLimit > 0 && <Text type="secondary" style={{ marginLeft: 8 }}>显示最近 {historyList.length} 条</Text>}
            </Col>
          </Row>
          
          {runDetails && (
            <>
              {isCurrentRunDistributed && (
                <div style={{ marginBottom: 16, padding: '8px 16px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
                  <Text type="success"><strong>此运行记录已分配完成</strong> - 可查看分配结果，但不能修改</Text>
                </div>
              )}
              {runDetails.distributionPending && !isCurrentRunDistributed && (
                <div style={{ marginBottom: 16, padding: '8px 16px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6 }}>
                  <Text style={{ color: '#d46b08' }}><ClockCircleOutlined style={{ marginRight: 8 }} /><strong>此运行记录已提交分配，正在等待管理员确认</strong></Text>
                </div>
              )}
              {runDetails.distributionRejected && !isCurrentRunDistributed && (
                <div style={{ marginBottom: 16, padding: '8px 16px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
                  <Text type="danger">
                    <strong>分配被拒绝</strong>
                    {runDetails.distributionRejectionReason && ` - 原因：${runDetails.distributionRejectionReason}`}
                    <br />
                    <Text type="secondary">你可以重新计算并提交分配</Text>
                  </Text>
                </div>
              )}
              {!isCurrentRunDistributed && (
                <Row gutter={16} align="middle">
                  <Col>
                    <Checkbox
                      checked={appleEnabled}
                      onChange={e => {
                        setAppleEnabled(e.target.checked);
                        if (!e.target.checked) {
                          setAppleConsumption([]);
                        }
                      }}
                      disabled={!canEdit}
                    >
                      <Space>
                        <img src="/apple.png" alt="apple" style={{ width: 16, height: 16, verticalAlign: 'middle' }} />
                        启用苹果消耗分配
                      </Space>
                    </Checkbox>
                    {appleEnabled && appleSummary.totalCost > 0 && (
                      <Text type="secondary" style={{ marginLeft: 12 }}>
                        (苹果消耗: <Text style={{ color: '#ff4d4f' }}>-{appleSummary.totalCost.toLocaleString()}</Text> Meso)
                      </Text>
                    )}
                  </Col>
                </Row>
              )}
              <Row gutter={16} align="middle">
                <Col>
                  <Space>
                    <DollarOutlined style={{ color: '#52c41a' }} />
                    <Text>Meso 总额：</Text>
                    <InputNumber
                      value={mesoAmount}
                      onChange={v => setMesoAmount(v ?? 0)}
                      style={{ width: 180 }}
                      formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={v => v.replace(/\$\s?|(,*)/g, '')}
                      disabled={isCurrentRunDistributed}
                    />
                  </Space>
                </Col>
                <Col>
                  <Space>
                    <GoldOutlined style={{ color: '#faad14' }} />
                    <Text>Coin 总额：</Text>
                    <InputNumber
                      value={coinAmount}
                      onChange={v => setCoinAmount(v || 0)}
                      min={0}
                      style={{ width: 150 }}
                      formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={v => v.replace(/\$\s?|(,*)/g, '')}
                      disabled={isCurrentRunDistributed}
                    />
                  </Space>
                </Col>
                <Col>
                  <Button type="primary" icon={<CalculatorOutlined />} onClick={calculateDistribution} disabled={isCurrentRunDistributed || runDetails?.distributionPending}>
                    计算分配
                  </Button>
                </Col>
                <Col>
                  <Button 
                    type="primary" 
                    icon={<SaveOutlined />} 
                    onClick={saveDistribution}
                    loading={loading}
                    style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    disabled={isCurrentRunDistributed || runDetails?.distributionPending}
                  >
                    提交分配
                  </Button>
                </Col>
              </Row>

              {/* 自定义罚款：勾选角色 → 填罚款额 → 并入本次分配（允许扣到负）*/}
              {canEdit && (
                <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                  <Space wrap align="center">
                    <Text strong style={{ color: '#d46b08' }}>自定义罚款：</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>已勾选 {penaltyRows.length} 人</Text>
                    <Space>
                      <DollarOutlined style={{ color: '#ff4d4f' }} />
                      <Text>Meso</Text>
                      <InputNumber
                        value={penaltyMeso}
                        onChange={v => setPenaltyMeso(v ?? 0)}
                        min={0}
                        style={{ width: 150 }}
                        formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={v => v.replace(/\$\s?|(,*)/g, '')}
                        placeholder="罚款 Meso"
                      />
                    </Space>
                    <Space>
                      <GoldOutlined style={{ color: '#ff4d4f' }} />
                      <Text>Coin</Text>
                      <InputNumber
                        value={penaltyCoin}
                        onChange={v => setPenaltyCoin(v ?? 0)}
                        min={0}
                        style={{ width: 120 }}
                        formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={v => v.replace(/\$\s?|(,*)/g, '')}
                        placeholder="罚款 Coin"
                      />
                    </Space>
                    <Button danger type="primary" onClick={applyPenalty} disabled={penaltyRows.length === 0}>
                      应用罚款
                    </Button>
                    <Text type="secondary" style={{ fontSize: 11 }}>从选中角色本次收益扣除，可扣为负；应用后随「提交分配」一起生效</Text>
                  </Space>
                </div>
              )}

              <Table
                dataSource={distributions}
                columns={distributionColumns}
                rowKey="characterName"
                size="small"
                pagination={false}
                rowSelection={canEdit ? {
                  selectedRowKeys: penaltyRows,
                  onChange: (keys) => setPenaltyRows(keys),
                } : undefined}
                summary={() => {
                  const totalMeso = distributions.reduce((s, d) => s + d.meso, 0);
                  const totalCoin = distributions.reduce((s, d) => s + d.coin, 0);
                  const totalRounds = distributions.reduce((s, d) => s + d.roundCount, 0);
                  const mesoColor = totalMeso > 0 ? '#52c41a' : (totalMeso < 0 ? '#ff4d4f' : undefined);
                  const coinColor = totalCoin > 0 ? '#faad14' : (totalCoin < 0 ? '#ff4d4f' : undefined);
                  return (
                    <Table.Summary.Row style={{ background: 'var(--surface-subtle)', fontWeight: 'bold' }}>
                      {canEdit && <Table.Summary.Cell index={-1} />}
                      <Table.Summary.Cell index={0}>合计</Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>{distributions.length} 人</Table.Summary.Cell>
                      <Table.Summary.Cell index={2}></Table.Summary.Cell>
                      <Table.Summary.Cell index={3}></Table.Summary.Cell>
                      <Table.Summary.Cell index={4}><Tag color="purple">{totalRounds} Rounds</Tag></Table.Summary.Cell>
                      <Table.Summary.Cell index={5}><Text style={{ color: mesoColor }}>{totalMeso.toLocaleString()}</Text></Table.Summary.Cell>
                      <Table.Summary.Cell index={6}><Text style={{ color: coinColor }}>{totalCoin.toLocaleString()}</Text></Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            </>
          )}
          
          {!runDetails && (
            <Empty description="请选择一个历史运行记录来计算分配" />
          )}
        </Space>
      </Card>

      {/* 管理员审核区域 */}
      {isAdmin && pendingDistributions.length > 0 && (
        <Card 
          title={<><ClockCircleOutlined style={{ color: '#fa8c16' }} /> 待确认的分配 ({pendingDistributions.length})</>}
          style={{ marginBottom: 16, border: '2px solid #fa8c16' }}
          extra={<Button icon={<ReloadOutlined />} onClick={loadPendingDistributions}>刷新</Button>}
        >
          <Table
            dataSource={pendingDistributions}
            rowKey="runId"
            size="small"
            pagination={false}
            columns={[
              { title: '运行名称', dataIndex: 'runName', key: 'runName' },
              { 
                title: 'Meso 总额', 
                dataIndex: 'mesoTotal', 
                key: 'mesoTotal',
                render: v => <Text style={{ color: '#52c41a' }}>{(v || 0).toLocaleString()}</Text>
              },
              { 
                title: 'Coin 总额', 
                dataIndex: 'coinTotal', 
                key: 'coinTotal',
                render: v => <Text style={{ color: '#faad14' }}>{(v || 0).toLocaleString()}</Text>
              },
              { 
                title: '分配人数', 
                key: 'count',
                render: (_, record) => <Tag color="blue">{record.distributions?.length || 0} 人</Tag>
              },
              { title: '提交者', dataIndex: 'submittedBy', key: 'submittedBy' },
              { 
                title: '提交时间', 
                dataIndex: 'submittedAt', 
                key: 'submittedAt',
                render: t => new Date(t).toLocaleString()
              },
              {
                title: '操作',
                key: 'action',
                render: (_, record) => (
                  <Space>
                    <Button 
                      type="primary" 
                      size="small" 
                      icon={<CheckOutlined />}
                      onClick={() => approveDistribution(record.runId)}
                      loading={loading}
                    >
                      确认
                    </Button>
                    <Button 
                      danger 
                      size="small" 
                      icon={<CloseOutlined />}
                      onClick={() => openRejectModal(record.runId)}
                    >
                      拒绝
                    </Button>
                  </Space>
                )
              }
            ]}
            expandable={{
              expandedRowRender: (record) => (
                <Table
                  dataSource={record.distributions || []}
                  rowKey="characterName"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '角色', dataIndex: 'characterName', key: 'characterName' },
                    { title: '用户', dataIndex: 'username', key: 'username' },
                    { title: '队伍', dataIndex: 'team', key: 'team', render: t => <Tag>T{t}</Tag> },
                    { title: 'Meso', dataIndex: 'meso', key: 'meso', render: v => <Text style={{ color: '#52c41a' }}>{(v || 0).toLocaleString()}</Text> },
                    { title: 'Coin', dataIndex: 'coin', key: 'coin', render: v => <Text style={{ color: '#faad14' }}>{(v || 0).toLocaleString()}</Text> },
                  ]}
                />
              )
            }}
          />
        </Card>
      )}

      {/* 拒绝原因弹窗 */}
      <Modal
        title="拒绝分配"
        open={rejectModalVisible}
        onOk={rejectDistribution}
        onCancel={() => {
          setRejectModalVisible(false);
          setRejectReason('');
          setRejectingRunId(null);
        }}
        okText="确认拒绝"
        cancelText="取消"
        okButtonProps={{ danger: true, loading: loading }}
      >
        <p>请输入拒绝原因（可选）：</p>
        <Input.TextArea
          value={rejectReason}
          onChange={e => setRejectReason(e.target.value)}
          placeholder="例如：分配比例有误、物品价格不正确等"
          rows={3}
        />
      </Modal>

      {/* 银行总额统计卡片 */}
      <Card 
        style={{ 
          marginBottom: 16, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          border: 'none'
        }}
        styles={{ body: { padding: '24px 32px' } }}
      >
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 64
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8 }}>💰 银行 Meso 总额</div>
            <div style={{ color: '#fff', fontSize: 32, fontWeight: 'bold' }}>
              {characterBalances.reduce((sum, u) => sum + (u.mesoBalance || 0), 0).toLocaleString()}
            </div>
          </div>
          <div style={{ width: 1, height: 60, background: 'rgba(255,255,255,0.3)' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 8 }}>🪙 银行 Coin 总额</div>
            <div style={{ color: '#fff', fontSize: 32, fontWeight: 'bold' }}>
              {characterBalances.reduce((sum, u) => sum + (u.coinBalance || 0), 0).toLocaleString()}
            </div>
          </div>
        </div>
      </Card>

      {/* 用户余额 */}
      <Card 
        title={<><UserOutlined /> 用户余额</>} 
        style={{ marginBottom: 16 }}
        extra={<Button icon={<ReloadOutlined />} onClick={loadCharacterBalances}>刷新</Button>}
      >
        <Table
          dataSource={isAdmin ? characterBalances : characterBalances.filter(b => !b.isUnassigned)}
          columns={balanceColumns}
          rowKey="username"
          size="small"
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `共 ${total} 位用户`,
          }}
          locale={{ emptyText: '暂无用户余额数据' }}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => record.characters && record.characters.length > 0,
          }}
        />
      </Card>

      {/* 交易记录 */}
      <Card 
        title={<><HistoryOutlined /> 交易记录 <Tag color="blue">{transactions.length}</Tag></>}
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>显示最新 {transactionLimit} 条</Text>
            <Button icon={<ReloadOutlined />} onClick={() => loadTransactions(transactionLimit)}>刷新</Button>
          </Space>
        }
      >
        {groupedTransactions.length === 0 ? (
          <Empty description="暂无交易记录" />
        ) : (
          <Collapse
            defaultActiveKey={groupedTransactions[0]?.date}
            items={groupedTransactions.map(g => ({
              key: g.date,
              label: (
                <span>
                  <Text strong>{g.date}</Text>
                  <Tag color="blue" style={{ marginLeft: 8 }}>{g.items.length} 条</Tag>
                </span>
              ),
              children: (
                <Table
                  dataSource={g.items}
                  columns={transactionColumns}
                  rowKey={(r, i) => `${r.runId}-${r.username}-${r.type}-${i}`}
                  size="small"
                  pagination={false}
                />
              ),
            }))}
          />
        )}
        {hasMoreTransactions && transactions.length >= transactionLimit && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button type="link" onClick={loadMoreTransactions}>
              加载更多交易记录
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default BonusBank;
